import { useState } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Link } from 'react-router-dom';
import { Phone, ArrowRight, BookOpen, Activity } from 'lucide-react';

const LumbarRadiculopathySciaticaCondition = () => {
  const [openFaqId, setOpenFaqId] = useState(null);

  const toggleFaq = (id) => {
    setOpenFaqId(openFaqId === id ? null : id);
  };

  const pageData = {
    title: "Lumbar Radiculopathy (Sciatica) in New York City",
    metaDescription: "Effective treatment for lumbar radiculopathy (sciatica) in New York. Learn about therapies to relieve radiating leg pain and restore mobility at NY Center for Spinal Disorders.",
    metaKeywords: "lumbar radiculopathy, sciatica treatment NYC, pinched nerve, Dr. Shah, Dr. Tindel, leg pain relief, spinal nerve irritation",
    heroDescription: "Comprehensive care for lumbar radiculopathy (sciatica) with personalized treatment plans to alleviate pain and improve function.",
    forWho: [
      "Lumbar radiculopathy, commonly called sciatica, refers to irritation or compression of the nerve roots in the lower back. These nerves travel down the buttocks and legs, so when they’re pinched or inflamed, you may feel sharp, shooting pain that radiates down one leg, along with numbness or tingling.",
      "Herniated discs, bone spurs, or a narrowing of the spinal canal (spinal stenosis) often contribute to sciatica. It can strike people of different ages, though it’s most prevalent in those with degenerative changes in the spine or who repeatedly stress their lower back."
    ],
    conditionInfo: [
      "If sciatica is disrupting your life—making it difficult to walk, sit, or stand comfortably—our team at the NY Center for Spinal Disorders in Manhattan can pinpoint the exact cause and guide you toward a plan to relieve nerve pressure and restore mobility.",
    ],
    treatment: [
      "Physical therapy, targeted exercises, and anti-inflammatory injections are first-line approaches.",
      "Surgery (like microdiscectomy) may be recommended if conservative treatment fails.",
      "Most patients see improvement over weeks to months with proper care."
    ],
    postCare: [
      "We schedule follow-ups and adapt treatments as your pain subsides.",
      "Home exercise programs support long-term spine health."
    ],
    nonSurgicalOptions: [
      "Physical therapy",
      "Epidural steroid injections",
      "Nerve blocks"
    ],
    faqs: [
      {
        id: 1,
        question: "Can sciatica heal without surgery?",
        answer: "Yes, many cases improve with conservative care over time."
      },
      {
        id: 2,
        question: "How soon can I drive again?",
        answer: "Typically within a few days or weeks, depending on pain control and mobility."
      }
    ]
  };  

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      <Helmet>
      <title>{pageData.title} - NYC for Spinal Disorders</title>
      <meta name="description" content={pageData.metaDescription} />
      <meta name="keywords" content={pageData.metaKeywords} />
      <meta name="author" content="New York Center for Spinal Disorders" />
      <meta property="og:title" content={pageData.title} />
      <meta property="og:description" content={pageData.metaDescription} />
      <meta property="og:image" content="/images/logo-header.png" />
      <meta property="og:type" content="website" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="viewport" content="width=device-width, initial-scale=1" />
      </Helmet>
      
      <Header />

      
      <div className="flex items-center relative min-h-[40vh] bg-gradient-to-r from-[#003666] to-[#0062B1] py-8">
        <div className="absolute inset-0">
          <img
            src="../images/circles.jpg"
            alt={pageData.title}
            className="w-full h-full object-cover opacity-20"
          />
        </div>
        <div className="relative h-full max-w-6xl mx-auto px-4 flex items-center">
          <div>
            <h1 className="text-5xl font-bold text-white mb-4">{pageData.title}</h1>
            <p className="text-2xl text-blue-100">{pageData.heroDescription}</p>
          </div>
        </div>
      </div>

      <div className="bg-white shadow-lg">
        <div className="max-w-6xl mx-auto py-6 px-4">
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact" className="inline-flex items-center px-6 py-3 bg-blue-900 text-white rounded-full hover:bg-blue-800 transition">
              Schedule Consultation <ArrowRight className="ml-2 w-4 h-4" />
            </Link>
            <a href="tel:+12122493840" className="inline-flex items-center px-6 py-3 border-2 border-blue-900 text-blue-900 rounded-full hover:bg-blue-50 transition">
              <Phone className="mr-2 w-4 h-4" /> Call Now
            </a>
          </div>
        </div>
      </div>

      <main className="max-w-6xl mx-auto px-4 py-12">
        <div className="bg-white rounded-xl shadow-lg p-8 mb-12">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-start gap-4">
              <BookOpen className="w-8 h-8 text-blue-600 flex-shrink-0" />
              <div>
                <h2 className="text-2xl font-bold text-blue-900 mb-4">Condition Overview</h2>
                {pageData.forWho.map((paragraph, index) => (
                  <p key={index} className="text-gray-700 leading-relaxed mb-4">{paragraph}</p>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-8 mb-12">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-start gap-4">
              <Activity className="w-8 h-8 text-blue-600 flex-shrink-0" />
              <div>
                <h2 className="text-2xl font-bold text-blue-900 mb-4">What Patients Need to Know</h2>
                <ul className="space-y-4 text-gray-700">
                  {pageData.conditionInfo.map((info, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <span className="text-blue-600 font-bold">•</span>
                      <span>{info}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>





        <div className="bg-gradient-to-r from-[#003666] to-[#0062B1] rounded-xl shadow-lg p-12 text-center">
          <h2 className="text-3xl font-bold text-white mb-6">Patient-Centered Approach</h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Our dedicated team of board-certified orthopedic spine physicians at New York Center for Spinal Disorders works together to guide you every step of the way, always prioritizing your comfort and well-being.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link to="/contact" className="inline-flex items-center justify-center px-8 py-4 bg-white text-blue-900 rounded-full font-bold hover:bg-blue-100 transition duration-300">
              Schedule Consultation
            </Link>
            <a href="tel:+12122493840" className="inline-flex items-center justify-center px-8 py-4 border-2 border-white text-white rounded-full font-bold hover:bg-white/10 transition duration-300">
              <Phone className="w-5 h-5 mr-2" />
              Call Now
            </a>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default LumbarRadiculopathySciaticaCondition;